import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { createIdbKvStore } from '../storage/idb-kv-store.util';

export interface IHostListingMeta {
  archived: boolean;
}

const META_KEY = 'cnt-host-listing-meta';
const META_IDB_KEY = 'host:listing-meta';

const EMPTY_META: IHostListingMeta = { archived: false };

@Injectable({ providedIn: 'root' })
export class HostListingMetaService {
  private platformId = inject(PLATFORM_ID);
  private auth = inject(AuthService);

  private readonly store = createIdbKvStore<Record<number, IHostListingMeta>>({
    legacyKeys: { [META_IDB_KEY]: META_KEY },
  });

  private readonly _meta$ = new BehaviorSubject<Record<number, IHostListingMeta>>({});
  readonly meta$: Observable<Record<number, IHostListingMeta>> = this._meta$.asObservable();

  constructor() {
    this._meta$.next(this.read());
    if (isPlatformBrowser(this.platformId)) {
      void this.store.init().then(() => this._meta$.next(this.read()));
      this.auth.loggedOut$.subscribe(() => this.clearAll());
    }
  }

  /** Drops every listing's meta entry. Called on sign-out. */
  clearAll(): void {
    this.write({});
  }

  get(listingId: number): IHostListingMeta {
    return this._meta$.value[listingId] || EMPTY_META;
  }

  setArchived(listingId: number, archived: boolean): void {
    this.patch(listingId, { archived });
  }

  /** Drop the meta entry for a listing. Called by the delete-listing flow. */
  clear(listingId: number): void {
    const next = { ...this._meta$.value };
    if (!(listingId in next)) return;
    delete next[listingId];
    this.write(next);
  }

  private patch(listingId: number, patch: Partial<IHostListingMeta>): void {
    const next = { ...this._meta$.value };
    next[listingId] = { ...(next[listingId] || EMPTY_META), ...patch };
    this.write(next);
  }

  private read(): Record<number, IHostListingMeta> {
    if (!isPlatformBrowser(this.platformId)) return {};
    return this.store.read(META_IDB_KEY) ?? {};
  }

  private write(meta: Record<number, IHostListingMeta>): void {
    if (isPlatformBrowser(this.platformId)) {
      void this.store.write(META_IDB_KEY, meta);
    }
    this._meta$.next(meta);
  }
}
